<template>
  <div class="max-w-md w-full mx-auto px-4 py-8">
    <div class="pwa-card p-6 md:p-8">
      <div class="text-center mb-8">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-500/10 text-indigo-400 mb-4 border border-indigo-500/20">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-slate-100">Event Registration</h2>
        <p class="text-sm text-slate-400 mt-2">
          {{ store.event?.name || 'Loading active event context...' }}
        </p>
      </div>

      <div v-if="store.loading && !store.event" class="flex flex-col items-center justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mb-4"></div>
        <span class="text-sm text-slate-400">Initializing event session...</span>
      </div>

      <form v-else @submit.prevent="handleRegister" class="space-y-5">
        <!-- Emergency registration closed block -->
        <div v-if="!store.isRegistrationOpen" class="p-6 bg-amber-500/10 border border-amber-500/20 rounded-xl text-center space-y-3">
          <div class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/10 text-amber-400 mb-1 border border-amber-500/20">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </div>
          <h3 class="text-lg font-bold text-amber-400 uppercase tracking-wider">REGISTRATION CLOSED</h3>
          <p class="text-xs text-slate-300">
            This event is currently sold out or registrations have been temporarily suspended by administration.
          </p>
          <div class="pt-2">
            <button type="button" @click="goToLogin" class="pwa-btn-secondary w-full">
              Go to Login Dashboard
            </button>
          </div>
        </div>

        <!-- Success Alert -->
        <div v-else-if="successMsg" class="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-sm text-emerald-400 text-center">
          <p class="font-semibold text-base mb-1">🎉 Registration Completed!</p>
          <p class="text-xs text-slate-300 mb-4">Please log in to claim your dashboard, ticketing, and quest options.</p>
          <button type="button" @click="goToLogin" class="pwa-btn-primary">
            Proceed to Login
          </button>
        </div>

        <!-- Main Form when open and not yet registered -->
        <template v-else>
          <!-- Error Alert -->
          <div v-if="store.error" class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400 flex items-start gap-2">
            <svg class="w-5 h-5 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <span class="font-semibold">Registration Error:</span>
              <p class="mt-0.5">{{ store.error }}</p>
            </div>
          </div>

          <!-- Referred By Notice -->
          <div v-if="referredBy" class="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg text-xs text-indigo-300 flex items-center gap-2">
            <svg class="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span>You are being referred by <strong>{{ referredBy }}</strong></span>
          </div>

          <!-- Conditional Community Selection -->
          <div v-if="communities && communities.length > 0">
            <label class="pwa-label" for="community_select">Afiliasi Komunitas / Kopdar (Opsional)</label>
            <select
              id="community_select"
              v-model="selectedCommunityId"
              class="pwa-input"
              @change="handleCommunityChange"
            >
              <option value="">Individu (Kirim Ke Alamat Rumah)</option>
              <option v-for="comm in communities" :key="comm.id" :value="comm.id">
                {{ comm.name }} (Drop Point Basecamp)
              </option>
            </select>
          </div>

          <!-- Basecamp Info Alert -->
          <div v-if="selectedCommunity" class="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl space-y-2">
            <div class="flex items-center gap-2 text-indigo-400 font-semibold text-sm">
              <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 100-6 3 3 0 000 6z" />
              </svg>
              <span>Basecamp Delivery Aktif: {{ selectedCommunity.name }}</span>
            </div>
            <p class="text-xs text-slate-300">
              Merchandise edisi terbatas Anda akan dikirim secara kolektif ke basecamp komunitas pada H-3. Anda tidak perlu memasukkan alamat rumah.
            </p>
            <div class="text-[11px] text-indigo-300/80 bg-indigo-500/5 p-2 rounded border border-indigo-500/10">
              <strong>Basecamp:</strong> {{ selectedCommunity.basecamp_address }} ({{ selectedCommunity.postal_code }})
            </div>
          </div>

          <!-- Basic Fields -->
          <div>
            <label class="pwa-label" for="whatsapp_number">WhatsApp Number</label>
            <input
              id="whatsapp_number"
              v-model="form.whatsapp_number"
              type="text"
              class="pwa-input"
              placeholder="e.g. 62812345678"
              required
            />
            <p class="text-xs text-slate-500 mt-1">Country code prefix without spaces or signs (+).</p>
          </div>

          <div>
            <label class="pwa-label" for="name">Full Name</label>
            <input
              id="name"
              v-model="form.name"
              type="text"
              class="pwa-input"
              placeholder="Enter your name"
              required
            />
          </div>

          <!-- Optional Standard Fields -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="pwa-label" for="shirt_size">Shirt Size</label>
              <select id="shirt_size" v-model="form.shirt_size" class="pwa-input" required>
                <option value="">Select size</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
              </select>
            </div>
            <div v-if="!selectedCommunity">
              <label class="pwa-label" for="postal_code">Postal Code</label>
              <input
                id="postal_code"
                v-model="form.postal_code"
                type="text"
                class="pwa-input"
                placeholder="Postal code"
                :required="!selectedCommunity"
              />
            </div>
          </div>

          <div v-if="!selectedCommunity">
            <label class="pwa-label" for="shipping_address">Shipping Address</label>
            <textarea
              id="shipping_address"
              v-model="form.shipping_address"
              rows="2"
              class="pwa-input"
              placeholder="Full shipping address"
              :required="!selectedCommunity"
            ></textarea>
          </div>

          <!-- Dynamic Custom Fields from Event Config -->
          <div v-if="dynamicFields && dynamicFields.length > 0" class="pt-4 border-t border-slate-800 space-y-4">
            <h3 class="text-xs font-semibold uppercase tracking-wider text-slate-500">Additional Fields</h3>
            
            <div v-for="field in dynamicFields" :key="field.name">
              <label class="pwa-label" :for="'custom_' + field.name">
                {{ field.label }}
                <span v-if="field.required" class="text-red-500">*</span>
              </label>

              <!-- Dropdown Select -->
              <select
                v-if="field.type === 'select'"
                :id="'custom_' + field.name"
                v-model="customFields[field.name]"
                class="pwa-input"
                :required="field.required"
              >
                <option value="">Choose an option</option>
                <option v-for="opt in field.options" :key="opt" :value="opt">
                  {{ opt }}
                </option>
              </select>

              <!-- Number Input -->
              <input
                v-else-if="field.type === 'number'"
                :id="'custom_' + field.name"
                v-model.number="customFields[field.name]"
                type="number"
                class="pwa-input"
                :required="field.required"
              />

              <!-- Default Text Input -->
              <input
                v-else
                :id="'custom_' + field.name"
                v-model="customFields[field.name]"
                type="text"
                class="pwa-input"
                :required="field.required"
              />
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="pt-4 space-y-3">
            <button type="submit" :disabled="store.loading" class="pwa-btn-primary">
              <span v-if="store.loading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              {{ store.loading ? 'Registering...' : 'Complete Registration' }}
            </button>

            <button type="button" @click="goToLogin" class="pwa-btn-secondary">
              Already registered? Login here
            </button>
          </div>
        </template>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useParticipantStore } from '../stores/participant'

const router = useRouter()
const route = useRoute()
const store = useParticipantStore()

const referredBy = computed(() => (route.query.ref as string) || '')

const form = ref({
  whatsapp_number: '',
  name: '',
  shirt_size: '',
  shipping_address: '',
  postal_code: '',
})

const selectedCommunityId = ref('')

const communities = computed(() => {
  return (store.event?.config as any)?.communities || []
})

const selectedCommunity = computed(() => {
  return communities.value.find((c: any) => c.id === selectedCommunityId.value) || null
})

function handleCommunityChange() {
  if (selectedCommunity.value) {
    form.value.shipping_address = `Drop Point: ${selectedCommunity.value.basecamp_address}`
    form.value.postal_code = selectedCommunity.value.postal_code
  } else {
    form.value.shipping_address = ''
    form.value.postal_code = ''
  }
}

const customFields = ref<Record<string, any>>({})
const successMsg = ref(false)

// Resolve registration fields from event settings configuration
const dynamicFields = computed(() => {
  return store.event?.config?.registration_fields || []
})

let pollInterval: any = null

onMounted(async () => {
  const slug = route.params.slug as string
  await store.loadEventContext(slug)

  // If a referral code was passed in query, see if it maps to any community
  const refCode = route.query.ref as string
  if (refCode && communities.value.length > 0) {
    const matchedComm = communities.value.find(
      (c: any) => c.referral_code.toUpperCase() === refCode.toUpperCase()
    )
    if (matchedComm) {
      selectedCommunityId.value = matchedComm.id
      handleCommunityChange()
    }
  }

  // Poll registration status every 15 seconds
  pollInterval = setInterval(async () => {
    await store.checkRegistrationStatus(slug)
  }, 15000)
})

onUnmounted(() => {
  if (pollInterval) {
    clearInterval(pollInterval)
  }
})

async function handleRegister() {
  store.clearError()
  const refCode = selectedCommunity.value?.referral_code || referredBy.value || undefined

  const payload = {
    ...form.value,
    custom_fields: {
      ...customFields.value,
      community_id: selectedCommunity.value?.id || null,
      community_name: selectedCommunity.value?.name || null,
    },
    referred_by_code: refCode,
  }

  const result = await store.register(payload)
  if (result) {
    successMsg.value = true
  }
}

function goToLogin() {
  router.push({
    name: 'login',
    params: { slug: store.event?.slug || route.params.slug }
  })
}
</script>
